import React, { useMemo } from "react";
import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import { Button, Typography } from "@mui/material";

function QuickSearchToolbar() {
    return (
        <Box
            sx={{
                p: 0.5,
                pb: 0,
            }}
        >
            <GridToolbarQuickFilter />
        </Box>
    );
}

export default function OperatorPMSProfilesTable(props) {
    // Transform API data to DataGrid format
    const pmsProfilesData = useMemo(() => {
        if (!props.pmsProfilesList || !Array.isArray(props.pmsProfilesList)) {
            return [];
        }
        
        return props.pmsProfilesList.map((profile, index) => ({
            id: index + 1, // DataGrid requires an id field
            ProfileID: profile.ProfileID,
            ProfileName: profile.ProfileName,
            Type: profile.Type,
            dateAddedUTC: profile.dateAddedUTC,
            dateModifiedUTC: profile.dateModifiedUTC,
            RealPageURL: profile.RealPageURL,
            RealPageAPIKey: profile.RealPageAPIKey,
            RealPagePMCId: profile.RealPagePMCId,
            EntrataAPIKey: profile.EntrataAPIKey,
            EntrataSubdomain: profile.EntrataSubdomain,
            // Keep original profile data for modify/delete operations
            originalProfile: profile
        }));
    }, [props.pmsProfilesList]);

    // Check if user is customer support
    const customerSupport = sessionStorage.getItem("customerSupport");
    const isDisabled = customerSupport && parseInt(customerSupport) > 0;

    const pmsProfileColumns = [
        {
            headerName: "Profile Name",
            field: "ProfileName",
            minWidth: 300,
            flex: 1,
        },
        {
            headerName: "PMS Type",
            field: "Type",
            minWidth: 150,
        },
        {
            headerName: "Created Date",
            field: "dateAddedUTC",
            minWidth: 180,
            valueFormatter: (value) => {
                if (value) {
                    try {
                        const date = new Date(value);
                        return date.toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                        });
                    } catch (error) {
                        console.error('Date formatting error:', error, value);
                        return value;
                    }
                }
                return "";
            },
        },
        {
            headerName: "Modified Date",
            field: "dateModifiedUTC",
            minWidth: 180,
            valueFormatter: (value) => {
                if (value) {
                    try {
                        const date = new Date(value);
                        return date.toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                        });
                    } catch (error) {
                        console.error('Date formatting error:', error, value);
                        return value;
                    }
                }
                return "";
            },
        },
        {
            headerName: "Action",
            field: "actions",
            minWidth: 300,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                return (
                    <div style={{ 
                        display: 'flex', 
                        gap: '8px',
                        pointerEvents: isDisabled ? 'none' : 'auto',
                        opacity: isDisabled ? 0.5 : 1
                    }}>
                        <Button
                            onClick={(e) => {
                                e.stopPropagation();
                                if (props.modifyPMSProfile) {
                                    props.modifyPMSProfile(params.row.originalProfile);
                                }
                            }}
                            variant="outlined"
                            size="small"
                        >
                            Modify
                        </Button>
                        <Button
                            onClick={(e) => {
                                e.stopPropagation();
                                if (props.deletePMSProfile) {
                                    props.deletePMSProfile(params.row.originalProfile);
                                }
                            }}
                            variant="outlined"
                            size="small"
                            color="error"
                        >
                            Delete
                        </Button>
                    </div>
                );
            },
        },
    ];

    // Show loading state
    if (props.loading) {
        return (
            <div style={{ height: 400, width: "100%", display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography>Loading PMS Profiles...</Typography>
            </div>
        );
    }

    // Show empty state
    if (!props.pmsProfilesList || props.pmsProfilesList.length === 0) {
        return (
            <div style={{ height: 400, width: "100%", display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography>No PMS Profiles found</Typography>
            </div>
        );
    }

    return (
        <div style={{ height: 400, width: "100%" }}>
            <DataGrid
                rows={pmsProfilesData}
                columns={pmsProfileColumns}
                initialState={{
                    pagination: {
                        paginationModel: { page: 0, pageSize: 10 },
                    },
                }}
                pageSizeOptions={[5, 10, 25]}
                slots={{ toolbar: QuickSearchToolbar }}
                disableRowSelectionOnClick
                sx={{
                    '& .MuiDataGrid-cell': {
                        fontSize: '0.875rem',
                    },
                    '& .MuiDataGrid-columnHeader': {
                        fontSize: '0.875rem',
                        fontWeight: 'bold',
                    },
                }}
            />
        </div>
    );
}